import { useAuth } from "@/context/AuthContext";
import { useCampaign } from "@/context/CampaignContext";
import { useSearch } from "@/context/SearchContext";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../context/AppContext";
import PromptData from "./PromptData";
import {
  ArrowRight,
  Loader2,
  Rocket,
  Search,
  Sparkles,
  Target,
  Users,
} from "lucide-react";
import {
  parseIntent,
  searchPeople,
} from "../../Redux/ReduxSlices/newAppoloApiSlice";
import { useDispatch } from "react-redux";
import SearchLoader from "../customeLoader/SearchLoader";
import { peoplePerPage } from "../../utils/perPageValue";
import { useEffect } from "react";

export default function ResultsList() {
  const {
    promptsDataCompany,
    setViewMode,
    promptsDataPeople,
    viewMode,
    setPromptsDataCompany,
    setPromptsDataPeople,
    setShowPromtData,
    setShowUrlData,
    showPromtData,
    isSearchLoading,
    setIsSearchLoading,
    loadingMessage,
    setLoadingMessage,
    promptInput,
    setPromptInput,
  } = useAppContext();

  const { setSelectedRecipients } = useCampaign();
  const { user } = useAuth();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePromptSearch = async () => {
    const promptInput = promptInput;
    // localStorage.getItem("lastPrompt");
    if (!promptInput) return;

    try {
      setIsSearchLoading(true);
      setLoadingMessage({
        title: "Searching our database...",
        subtitle: "Looking through existing leads and company data.",
      });

      const sessionId = localStorage.getItem("sessionId");

      const parseRes = await dispatch(
        parseIntent({
          user_id: user?.id || "test_user",
          session_id: sessionId,
          prompt: promptInput.trim(),
          use_goapi: true,
        })
      ).unwrap();

      const dbResults = await dispatch(
        searchPeople({
          parseIntentData: parseRes,
          page: 1,
          per_page: peoplePerPage,
        })
      ).unwrap();

      if (!dbResults || dbResults.length === 0) {
        setLoadingMessage({
          title: "Expanding your search...",
          subtitle: "Searching intelligently to uncover more opportunities...",
        });

        // 🔹 Optional fallback: run a wider web-based search
        const webResults = await dispatch(
          searchPeople({
            parseIntentData: parseRes,
            page: 1,
            per_page: peoplePerPage,
          })
        ).unwrap();

        setPromptsDataPeople(webResults);
      } else {
        setPromptsDataPeople(dbResults);
      }

      setViewMode("people");
      setShowPromtData(true);
      setShowUrlData(false);
      navigate("/search");
    } catch (error) {
      console.error("❌ Prompt search failed:", error);
    } finally {
      setIsSearchLoading(false);
    }
  };

  if (isSearchLoading) {
    return (
      <SearchLoader
        title={loadingMessage?.title}
        subtitle={loadingMessage?.subtitle}
      />
    );
  }

  const examplePrompts = [
    "General Manager of Hotels and Resorts in USA",
    "Importers of Home Décor",
    "Founder / Co-Founder / CEO of startups in India",
    "Senior Interior Designer in India",
  ];

  const handlePromptClick = (prompt) => {};

  return (
    <div className="space-y-3 w-full r">
      {/* Show empty message when nothing to show */}
      {!showPromtData && !isSearchLoading && (
        <div className="min-h-[500px] flex flex-col items-center justify-center px-6 py-12 text-center">
          {/* Animated Icon Container */}
          <div className="relative mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl flex items-center justify-center border border-blue-100 shadow-sm">
              <div className="relative">
                <Search className="w-6 h-6 text-blue-600" />
                <Sparkles className="w-3 h-3 text-blue-400 absolute -top-1 -right-1" />
              </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-3 -right-3 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center shadow-sm">
              <Target className="w-5 h-5 text-blue-500" />
            </div>
            <div className="absolute -bottom-3 -left-3 w-10 h-10 bg-indigo-100 rounded-full flex items-center justify-center shadow-sm">
              <Users className="w-5 h-5 text-indigo-500" />
            </div>
          </div>

          {/* Text Content */}
          <div className="max-w-2xl mx-auto">
            <h3 className=" mb-4 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Discover Your Perfect Leads
            </h3>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">
              Describe your ideal customers or provide a website URL to find
              relevant leads and company data with precision.
            </p>

            {/* Example Prompts Section */}
            <div className="mb-8">
              <div className="flex items-center justify-center gap-2 mb-4">
                <div className="w-4 h-0.5 bg-blue-200"></div>
                <span className="text-sm font-medium text-gray-500">
                  TRY THESE EXAMPLES
                </span>
                <div className="w-4 h-0.5 bg-blue-200"></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-3xl mx-auto">
                {examplePrompts.map((prompt, index) => (
                  <button
                    key={index}
                    onClick={() => setPromptInput(prompt)}
                    className="group p-4 text-left bg-white border border-gray-200 rounded-xl hover:border-blue-300 hover:shadow-md transition-all duration-200 hover:translate-y-[-2px]"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-6 h-6 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 group-hover:bg-blue-100 transition-colors">
                        <ArrowRight className="w-3 h-3 text-blue-500" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-gray-900 leading-relaxed">
                        {prompt}
                      </span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Actual data */}
      {showPromtData && <PromptData />}
    </div>
  );
}
